import React, { useState } from 'react';
import { Alert, Box, Button, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { EmptyState, ErrorState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import { fetchReferrals, portalErrorMessage, submitReferral } from '../../../../services/request/CustomerPortal';
import { formatDate, indianMobile, inr, PortalValidationKey, referralError, wholeRupees } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalReferrals() {
  const { t } = useTranslation();
  const referrals = useAsync(fetchReferrals, []);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [problem, setProblem] = useState<PortalValidationKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const input = {
      prospectName: name,
      prospectEmail: email,
      prospectPhone: indianMobile(phone) ?? phone,
      amountRequired: wholeRupees(amount) ?? 0,
    };
    const invalid = referralError(input);
    setProblem(invalid ?? null);
    if (invalid) {
      setError(null);
      return;
    }
    setSending(true);
    setError(null);
    try {
      await submitReferral(input);
      setNotice(t('portal.referrals.thanks', { name: name.trim() }));
      setName('');
      setEmail('');
      setPhone('');
      setAmount('');
      referrals.reload();
    } catch (err) {
      setError(portalErrorMessage(err, t('portal.referrals.sendError')));
    } finally {
      setSending(false);
    }
  };

  const message = problem ? t(`portal.validation.${problem}`) : error;

  return (
    <Box>
      <PageTitle kicker={t('portal.referrals.kicker')} title={t('portal.referrals.title')} subtitle={t('portal.referrals.subtitle')} />
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        <Paper component="form" onSubmit={submit} sx={{ p: 3, display: 'grid', gap: 2 }}>
          {notice ? (
            <Alert severity="success" onClose={() => setNotice(null)}>
              {notice}
            </Alert>
          ) : null}
          {message ? <Alert severity="error">{message}</Alert> : null}
          <TextField label={t('portal.referrals.fullName')} value={name} onChange={event => setName(event.target.value)} inputProps={{ maxLength: 120 }} />
          <TextField label={t('portal.referrals.email')} type="email" value={email} onChange={event => setEmail(event.target.value)} />
          <TextField
            label={t('portal.referrals.mobile')}
            value={phone}
            onChange={event => setPhone(event.target.value)}
            inputMode="tel"
            InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
          />
          <TextField
            label={t('portal.referrals.amount')}
            value={amount}
            onChange={event => setAmount(event.target.value)}
            inputMode="numeric"
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <Button type="submit" variant="contained" size="large" disabled={sending}>
            {sending ? t('common.sending') : t('portal.referrals.submit')}
          </Button>
        </Paper>

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('portal.referrals.listTitle')}
          </Typography>
          {referrals.error ? <ErrorState message={referrals.error} onRetry={referrals.reload} /> : null}
          {referrals.data && referrals.data.length === 0 ? <EmptyState title={t('portal.referrals.empty')} /> : null}
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {(referrals.data ?? []).map(referral => (
              <Paper key={referral.id} sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{referral.prospectName}</Typography>
                  <StatusChip status={referral.status} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {inr(referral.amountRequired)} · {referral.prospectEmail} · {formatDate(referral.createdDate) ?? ''}
                </Typography>
                {referral.adminNote ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('portal.common.noteFromWhatzneu', { note: referral.adminNote })}
                  </Typography>
                ) : null}
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
